import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, forkJoin } from 'rxjs';

export interface DemoSubject {
  id: number;
  subject: string;
}

export interface DemoLink {
  type: string;
  url: string;
  label: string;
}

export interface JupyterDemo {
  id: number;
  demo: string;
  description: string;
  subjects: DemoSubject[];
  links: DemoLink[];
}

export interface JupyterDemosResponse {
  name: string;
  demos: JupyterDemo[];
}

export interface FilterSubject {
  subjseq: number;
  subject: string;
}

export interface FilterCategory {
  category: string;
  subjects: FilterSubject[];
}

export interface FiltersResponse {
  name: string;
  categories: FilterCategory[];
}

@Injectable({
  providedIn: 'root'
})
export class JupyterDemosService {
  private readonly DEMOS_URL = 'https://storage.googleapis.com/clearscape-public/notebooks/jupyter-demos.json';
  private readonly FILTERS_URL = 'https://storage.googleapis.com/clearscape-public/notebooks/filters.json';

  constructor(private http: HttpClient) { }

  getDemos(): Observable<JupyterDemo[]> {
    return this.http.get<JupyterDemosResponse>(this.DEMOS_URL).pipe(
      map(response => response.demos)
    );
  }

  getFilters(): Observable<FilterCategory[]> {
    return this.http.get<FiltersResponse>(this.FILTERS_URL).pipe(
      map(response => response.categories)
    );
  }

  getFilterCategory(categories: FilterCategory[], categoryName: string): FilterSubject[] {
    const category = categories.find(c => c.category === categoryName);
    return category ? category.subjects.sort((a, b) => a.subjseq - b.subjseq) : [];
  }

  getFilteredDemos(
    demos: JupyterDemo[],
    filters: {
      industry?: string;
      analyticFunction?: string;
      thirdPartyTool?: string;
      language?: string;
      solutionAccelerator?: string;
      cloudProvider?: string;
    }
  ): JupyterDemo[] {
    let filtered = demos;

    if (filters.industry) {
      filtered = filtered.filter(demo => 
        demo.subjects.some(s => s.subject.toLowerCase().includes(filters.industry!.toLowerCase()))
      );
    }

    if (filters.analyticFunction) {
      filtered = filtered.filter(demo => 
        demo.subjects.some(s => s.subject.toLowerCase().includes(filters.analyticFunction!.toLowerCase()))
      );
    }

    if (filters.thirdPartyTool) {
      filtered = filtered.filter(demo => 
        demo.subjects.some(s => s.subject.toLowerCase().includes(filters.thirdPartyTool!.toLowerCase()))
      );
    }

    if (filters.language) {
      filtered = filtered.filter(demo => 
        demo.subjects.some(s => s.subject.toLowerCase().includes(filters.language!.toLowerCase()))
      );
    }

    if (filters.solutionAccelerator) {
      filtered = filtered.filter(demo => 
        demo.subjects.some(s => s.subject.toLowerCase().includes(filters.solutionAccelerator!.toLowerCase()))
      );
    }

    if (filters.cloudProvider) {
      filtered = filtered.filter(demo => 
        demo.subjects.some(s => s.subject.toLowerCase().includes(filters.cloudProvider!.toLowerCase()))
      );
    }

    return filtered;
  }

  getUniqueSubjects(demos: JupyterDemo[]): string[] {
    const subjectsSet = new Set<string>();
    demos.forEach(demo => {
      demo.subjects.forEach(subject => {
        subjectsSet.add(subject.subject);
      });
    });
    return Array.from(subjectsSet).sort();
  }

  /**
   * Check if a demo has nbviewer link (indicating it's a published demo)
   */
  hasNbviewerLink(demo: JupyterDemo): boolean {
    return demo.links && demo.links.some(link => 
      link.type === 'nbviewer' || link.url.includes('nbviewer')
    );
  }
}
